import { userAuthContextType } from '../../auth/AuthProvider';
import { axios, settings, getResponse } from '../../helpers/main';

const { backend } = settings;
const authServices = { user: userAuthContextType }; //eslint-disable-line
const objectService = getResponse({ type: 0 }); //eslint-disable-line

/**
 * Service to manage communication with the Companies API.
 * Follows the Single Responsibility Principle (SRP) by separating network logic from the UI.
 */
export const CompanyService = {
    /**
     * Retrieves all Companies.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    getAll: async ({
        auth,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.companies}/`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                method: backend?.methods?.companies.getAll,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Retrieves a Company by ID.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number|string} params.id - Company ID.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    getById: async ({
        auth,
        id,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.companies}/`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                method: backend?.methods?.companies.getById,
                id: id,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Creates a new Company.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {Object} params.data - Company data to create.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    create: async ({
        auth,
        data,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.companies}/`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                data: data,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Updates an existing Company.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number|string} params.id - Company ID.
     * @param {Object} params.data - Company data to update.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    update: async ({
        auth,
        id,
        data,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.companies}/${id}`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                data: data,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Deletes a Company by ID.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number|string} params.id - Company ID to delete.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    deleteAsync: async ({
        auth,
        id,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.companies}/${id}`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.delete(requestObj);
    },
};
